import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  useMediaQuery,
  Autocomplete,
  Field,
  Flex,
  Icon,
  Type,
} from "discover.medical.react-component-library";
import {
  DrugInteractionCheckerLegend,
  DrugInteractionInputContainer,
  DrugInteractionCheckerRow,
  DrugInteractionCheckerRemoveButton,
  DrugInteractionCheckerAddButton,
  DrugInteractionCheckButton,
  DrugInteractionResetButton,
  DrugInteractionCheckerHeader,
} from "./styled";
import {
  addInput,
  fetchDrugInteractionResults,
  removeInput,
} from "../../../store/drug-interactions/actions";
import { useStore, useDispatch } from "../../../store";

import { useAutocomplete } from "./hooks";
import DrugInteractionsErrorToast from "../ErrorToast";
import {
  logCheckerForInteractionsBtnClicked,
  logDICheckerPageLoaded,
} from "../../../util/analytics";
import { getDITelemetryProperties } from "../utils/checkerTelemetry";
import isEqual from "lodash/isEqual";
const renderSuggestion = (drug) => <Type>{drug.completion}</Type>;

const DrugInteractionsChecker = ({ viewModel }) => {
  const {
    drugInteractions: {
      inputs,
      defaultNumberOfInputs,
      autosuggest,
      cachedDrugsCodes,
    },
  } = useStore();
  const { suggestions, searchCondition, hasError } = autosuggest;
  const { header, content } = viewModel;
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("max", "sm");
  const removeDrugIcon = { glyph: content?.removeDrug?.icon?.glyph };
  const completionsURL = content?.autocomplete?.functionArgs?.apiPath;
  const isActionButtonDisabled = useMemo(
    () => inputs.some((input) => !input.selection),
    [inputs]
  );

  const handleAddClick = () => addInput(dispatch);
  const handleRemoveInput = (id) => removeInput(dispatch, id);

  const handleCheckInteractions = () => {
    const drugs = inputs.map((input) => input.selection.metadata);
    if (
      isEqual(
        cachedDrugsCodes,
        drugs.map((item) => item.code)
      )
    ) {
      return;
    }
    fetchDrugInteractionResults(
      dispatch,
      content?.check?.functionArgs?.apiPath,
      drugs
    );

    logCheckerForInteractionsBtnClicked(getDITelemetryProperties(inputs));
  };

  const isRemoveVisible = inputs.length > defaultNumberOfInputs;
  const isResetVisible = useMemo(
    () =>
      inputs.length > defaultNumberOfInputs ||
      inputs.some((input) => input.value),
    [inputs, defaultNumberOfInputs]
  );

  useEffect(() => {
    logDICheckerPageLoaded();
  }, []);

  const {
    setInputRef,
    handleChange,
    handleBlur,
    getSuggestionValue,
    shouldRenderSuggestions,
    handleSuggestionSelected,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    buildDisplayPlaceholder,
    handleReset,
  } = useAutocomplete({ dispatch, inputs, searchCondition, completionsURL });

  return (
    <Flex container column>
      <DrugInteractionCheckerHeader container column align={{ item: "center" }}>
        <Type element="h1" color="gray800" fontFamily="secondary">
          {header.title}
        </Type>
      </DrugInteractionCheckerHeader>

      <DrugInteractionInputContainer>
        <Field
          set
          id="drugInteractionChecker"
          label={
            <DrugInteractionCheckerLegend element="p">
              {content.infoMessage}
            </DrugInteractionCheckerLegend>
          }
        >
          {inputs.map((item, index) => {
            const placeholder = buildDisplayPlaceholder(
              content?.fieldPlaceholder,
              index
            );

            return (
              <DrugInteractionCheckerRow key={item.id} container width="100%">
                <Autocomplete
                  id={item.id}
                  index={index}
                  highlightFirstSuggestion
                  suggestions={suggestions}
                  renderSuggestion={renderSuggestion}
                  getSuggestionValue={getSuggestionValue}
                  autocompleteRef={(input) => setInputRef(input, index)}
                  onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                  onSuggestionsClearRequested={onSuggestionsClearRequested}
                  shouldRenderSuggestions={(value) =>
                    shouldRenderSuggestions(value, index)
                  }
                  onSuggestionSelected={(e, suggestion) =>
                    handleSuggestionSelected(e, suggestion, index)
                  }
                  inputProps={{
                    id: `input-${index}`,
                    name: "search",
                    type: "search",
                    value: item.value,
                    placeholder,
                    size: "md",
                    onBlur: () => handleBlur(index),
                    onChange: (_e, value) => handleChange(value, index),
                  }}
                />
                {isRemoveVisible && (
                  <DrugInteractionCheckerRemoveButton
                    variant="light"
                    kind="text"
                    size="iconOnly"
                    onClick={() => handleRemoveInput(item.id)}
                    aria-label={`Remove drug ${index + 1}`}
                    type="button"
                    data-testid="di-remove-drug"
                  >
                    <Icon icon={removeDrugIcon} />
                  </DrugInteractionCheckerRemoveButton>
                )}
              </DrugInteractionCheckerRow>
            );
          })}
        </Field>
        <DrugInteractionCheckerRow container width="100%">
          <DrugInteractionCheckerAddButton
            kind="text"
            size="md"
            onClick={handleAddClick}
            disabled={isActionButtonDisabled}
            type="button"
          >
            <Icon icon={{ glyph: content.addDrug.icon.glyph }} />
            {content.addDrug.label}
          </DrugInteractionCheckerAddButton>
        </DrugInteractionCheckerRow>
        <DrugInteractionCheckButton
          size="md"
          disabled={isActionButtonDisabled}
          children={content.check.label}
          onClick={handleCheckInteractions}
          data-testid="check-drug-interaction"
          type="button"
        />
      </DrugInteractionInputContainer>
      {isResetVisible && (
        <Flex
          container
          reverse
          justify={{ content: isMobile ? "center" : "flex-start" }}
          data-testid="drug-reset-container"
        >
          <DrugInteractionResetButton kind="anchor" onClick={handleReset}>
            {content.startOver.label}
          </DrugInteractionResetButton>
        </Flex>
      )}
      <DrugInteractionsErrorToast
        hasError={hasError}
        errorMessage={viewModel.content.errorMsg}
      />
    </Flex>
  );
};

DrugInteractionsChecker.displayName = "DrugInteractionChecker";

DrugInteractionsChecker.propTypes = {
  viewModel: PropTypes.shape({
    header: PropTypes.shape({
      label: PropTypes.string,
    }),
    content: PropTypes.shape({
      infoMessage: PropTypes.string,
      fieldPlaceholder: PropTypes.string,
      addDrug: PropTypes.shape({
        icon: PropTypes.shape({
          glyph: PropTypes.string,
          color: PropTypes.string,
          size: PropTypes.string,
        }),
        label: PropTypes.string,
        function: PropTypes.string,
        functionArgs: PropTypes.shape({
          apiPath: PropTypes.string,
          telemetryData: PropTypes.object,
        }),
      }),
      removeDrug: PropTypes.shape({
        icon: PropTypes.shape({
          glyph: PropTypes.string,
          color: PropTypes.string,
          size: PropTypes.string,
        }),
        function: PropTypes.string,
        functionArgs: PropTypes.shape({
          apiPath: PropTypes.string,
          telemetryData: PropTypes.object,
        }),
      }),
      startOver: PropTypes.shape({
        label: PropTypes.string,
        function: PropTypes.string,
        functionArgs: PropTypes.shape({
          apiPath: PropTypes.string,
          telemetryData: PropTypes.object,
        }),
      }),
      check: PropTypes.shape({
        label: PropTypes.string,
        function: PropTypes.string,
        functionArgs: PropTypes.shape({
          apiPath: PropTypes.string,
          telemetryData: PropTypes.object,
        }),
      }),
    }),
  }),
};

export default DrugInteractionsChecker;
